import React, { memo, useCallback, useState } from 'react'
import styled from '@emotion/styled'
import PropTypes from 'prop-types'

import OtpCell from './OtpCell'

const OtpCellInput = styled(OtpCell)`
  width: 40px !important;
  height: 3rem;
  font-size: 2rem;
  margin: 0 3px;
  text-align: center;
  border-radius: 4px;
  border: 0;
  background-color: #eceff4;
`

const OtpChainComponent = ({ length, autoFocus, setOtp }) => {
  const [activeInput, setActiveInput] = useState(0)
  const [otpValues, setOtpValues] = useState(Array(length).fill(''))

  const changeCodeAtFocus = useCallback(
    (str) => {
      const updatedOtpValues = [...otpValues]

      // since it's a fixed, predetermined length array,
      // set the array of index of the currently selected container to the new string
      updatedOtpValues[activeInput] = str[0] || ''
      setOtpValues(updatedOtpValues)
      setOtp(updatedOtpValues.join(''))
    },
    [activeInput, otpValues]
  )

  const focusInput = useCallback(
    (inputIndex) => {
      const selectedIndex = Math.max(Math.min(length - 1, inputIndex), 0)
      setActiveInput(selectedIndex)
    },
    [length]
  )

  const focusPrevInput = useCallback(() => {
    focusInput(activeInput - 1)
  }, [activeInput, focusInput])

  const focusNextInput = useCallback(() => {
    focusInput(activeInput + 1)
  }, [activeInput, focusInput])

  const handleOnFocus = useCallback(
    (inputIndex) => () => {
      focusInput(inputIndex)
    },
    [focusInput]
  )

  const handleOnChange = useCallback(
    (e) => {
      const val = e.currentTarget.value
      if (!val) {
        e.preventDefault()
        return
      }

      changeCodeAtFocus(val)
      focusNextInput()
    },
    [changeCodeAtFocus, focusNextInput]
  )

  const onBlur = useCallback(() => {
    setActiveInput((input) => {
      return input - 1 > 0 ? input - 1 : 0
    })
  }, [])

  const handleKeyDown = useCallback(
    (e) => {
      const pressedKey = e.key

      switch (pressedKey) {
        case 'Backspace':
        case 'Delete': {
          e.preventDefault()
          if (otpValues[activeInput]) {
            changeCodeAtFocus('')
          } else {
            focusPrevInput()
          }
          break
        }
        case 'ArrowLeft': {
          e.preventDefault()
          focusPrevInput()
          break
        }
        case 'ArrowRight': {
          e.preventDefault()
          focusNextInput()
          break
        }
        default: {
          if (pressedKey.match(/^[^a-zA-Z0-9]$/)) {
            e.preventDefault()
          }
          break
        }
      }
    },
    [activeInput, changeCodeAtFocus, focusNextInput, focusPrevInput, otpValues]
  )

  const handlePaste = useCallback(
    (e) => {
      e.preventDefault()
      const pastedData = e.clipboardData
        .getData('text/plain')
        .trim()
        .slice(0, length - activeInput)
        .split('')

      if (pastedData) {
        let nextFocusIndex = 0
        const updatedOtpValues = [...otpValues]
        updatedOtpValues.forEach((val, index) => {
          if (index >= activeInput) {
            const changedValue = pastedData.shift() || val
            if (changedValue) {
              updatedOtpValues[index] = changedValue
              nextFocusIndex = index
            }
          }
        })
        setOtpValues(updatedOtpValues)
        setActiveInput(Math.min(nextFocusIndex + 1, length - 1))
        setOtp(updatedOtpValues.join(''))
      }
    },
    [activeInput, length, otpValues]
  )

  return (
    <div>
      {Array(length)
        .fill('')
        .map((_, index) => (
          <OtpCellInput
            key={`SingleInput-${index}`}
            data-testid={`SingleInput-${index}`}
            type='text'
            focus={activeInput === index}
            value={otpValues && otpValues[index]}
            autoFocus={autoFocus}
            onFocus={handleOnFocus(index)}
            onChange={handleOnChange}
            onKeyDown={handleKeyDown}
            onBlur={onBlur}
            onPaste={handlePaste}
            disabled={activeInput !== index}
          />
        ))}
    </div>
  )
}

const OtpChain = memo(OtpChainComponent)

OtpChainComponent.propTypes = {
  length: PropTypes.number,
  autoFocus: PropTypes.bool,
  setOtp: PropTypes.func
}

export default OtpChain
